import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Check, Minus } from 'lucide-react';
import { PACKAGE_LIST, PACKAGE_FEATURES, packageIncludes } from '@/lib/realEstatePackages';

const bookLink = (code) => `/book?type=home&package=${code}`;
const tid = (code) => code.replace(/_/g, '-');

// Links to /book by default; inside the booking flow `pick` swaps it for an in-place choose button.
const Cta = ({ p, pick, label, testId, className }) => {
  if (!pick) {
    return <Button asChild className={className} data-testid={testId}><Link to={bookLink(p.code)}>{label}</Link></Button>;
  }
  const ok = pick.isAvailable(p.code);
  return (
    <Button type="button" disabled={!ok} onClick={() => pick.onSelect(p.code)} className={`${className} disabled:opacity-40`} data-testid={testId}>
      {!ok ? 'Not for this size' : pick.current === p.code ? 'Selected' : 'Choose'}
    </Button>
  );
};
const cellCls = (p) => (p.popular
  ? 'p-4 md:p-5 text-center text-gray-600 text-sm border-x-2 border-[#C6A14A] bg-[#C6A14A]/5 font-medium shadow-[0_0_20px_rgba(198,161,74,0.08)]'
  : 'p-4 md:p-5 text-center text-gray-600 text-sm border-l border-gray-100 transition-all duration-300 group-hover:bg-gray-50/50');

const TableRow = ({ label, note, render }) => (
  <div className="grid grid-cols-4 border-b border-gray-100 group">
    <div className="p-4 md:p-5 bg-gray-50 font-medium text-gray-700 text-sm">
      {label}{note && <span className="block text-xs text-gray-400 font-normal mt-1">{note}</span>}
    </div>
    {PACKAGE_LIST.map((p) => <div key={p.code} className={cellCls(p)}>{render(p)}</div>)}
  </div>
);

const Mark = ({ on, popular }) => (on
  ? <Check className={`w-5 h-5 mx-auto ${popular ? 'text-[#C6A14A]' : 'text-[#F5C056]'}`} />
  : <Minus className="w-5 h-5 text-gray-300/60 mx-auto" />);

const HeaderCell = ({ p }) => (p.popular ? (
  <div className="relative">
    <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20">
      <span className="inline-block bg-[#C6A14A] text-[#050816] text-[10px] font-bold uppercase tracking-wider px-4 py-1.5 rounded-full shadow-lg whitespace-nowrap">Most Popular</span>
    </div>
    <div className="p-6 text-center bg-gradient-to-b from-[#C6A14A]/20 to-[#C6A14A]/10 border-x-2 border-t-2 border-[#C6A14A] rounded-t-xl -mt-2 pt-8 shadow-[0_0_30px_rgba(198,161,74,0.15)]">
      <h3 className="font-serif text-lg mb-1 text-[#F5C056]">{p.name}</h3>
      <p className="text-2xl font-bold text-[#F5C056]">${p.price}</p>
      <p className="text-xs text-gray-300 mt-1.5">{p.tagline}</p>
    </div>
  </div>
) : (
  <div className="p-6 text-center border-r border-white/10 last:border-r-0 transition-all duration-300 hover:bg-white/5 group">
    <h3 className="font-serif text-lg mb-1 group-hover:text-[#F5C056] transition-colors">{p.name}</h3>
    <p className="text-2xl font-bold text-[#F5C056]">${p.price}</p>
    <p className="text-xs text-gray-400 mt-1.5">{p.tagline}</p>
  </div>
));

export const PackageTable = ({ pick, idPrefix = 'package' }) => (
  <div className="hidden md:block relative bg-white rounded-2xl shadow-lg border border-gray-200 overflow-visible" data-testid={`${idPrefix}-table`}>
    <div className="grid grid-cols-4 bg-[#050816] text-white rounded-t-2xl relative">
      <div className="p-6 border-r border-white/10"><p className="text-sm text-gray-400">Features</p></div>
      {PACKAGE_LIST.map((p) => <HeaderCell key={p.code} p={p} />)}
    </div>
    <TableRow label="Home Size" render={(p) => p.size_label} />
    <TableRow label="HDR Photos" render={(p) => <span data-testid={`${idPrefix}-${tid(p.code)}-photos`}>Up to {p.photos}</span>} />
    {PACKAGE_FEATURES.map((f) => (
      <TableRow key={f.key} label={f.label} note={f.note} render={(p) => <Mark on={p.features.includes(f.key)} popular={p.popular} />} />
    ))}
    <div className="grid grid-cols-4 rounded-b-2xl">
      <div className="p-5 bg-gray-50 rounded-bl-2xl" />
      {PACKAGE_LIST.map((p) => (
        <div key={p.code} className={p.popular ? 'p-5 text-center border-x-2 border-b-2 border-[#C6A14A] bg-[#C6A14A]/5 rounded-b-xl mb-[-8px] shadow-[0_4px_30px_rgba(198,161,74,0.2)]' : 'p-5 text-center border-l border-gray-100 transition-all duration-300 hover:-translate-y-1'}>
          <Cta p={p} pick={pick} label="Select" testId={`${idPrefix}-${tid(p.code)}-cta`}
            className={`w-full rounded-lg h-11 text-sm uppercase tracking-widest transition-all duration-300 hover:shadow-lg ${p.popular ? 'bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A] font-bold shadow-md' : 'bg-[#050816] text-white hover:bg-[#0a1020] font-medium'}`} />
        </div>
      ))}
    </div>
  </div>
);

const MobileCard = ({ p, pick, idPrefix }) => (
  <div className={p.popular ? 'relative bg-white rounded-2xl p-6 shadow-xl border-2 border-[#C6A14A]' : 'bg-white rounded-xl p-6 shadow-md border border-gray-200 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg'} data-testid={`${idPrefix}-card-${tid(p.code)}`}>
    {p.popular && (
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
        <span className="inline-block bg-[#C6A14A] text-[#050816] text-[10px] font-bold uppercase tracking-wider px-4 py-1.5 rounded-full shadow-lg">Most Popular</span>
      </div>
    )}
    <div className="text-center mb-4 pt-2">
      <h3 className="font-serif text-xl text-gray-900">{p.name}</h3>
      <p className={`text-3xl font-bold mt-2 ${p.popular ? 'text-[#C6A14A]' : 'text-[#F5C056]'}`}>${p.price}</p>
      <p className="text-xs text-gray-500 mt-1">{p.tagline}</p>
    </div>
    <ul className="space-y-3 mb-6">
      {packageIncludes(p).map((item) => (
        <li key={item} className="flex items-start gap-3 text-sm text-gray-600">
          <Check className={`w-4 h-4 mt-0.5 shrink-0 ${p.popular ? 'text-[#C6A14A]' : 'text-[#F5C056]'}`} />{item}
        </li>
      ))}
    </ul>
    <Cta p={p} pick={pick} label="Select Package" testId={`${idPrefix}-card-${tid(p.code)}-cta`}
      className={`w-full h-12 transition-colors ${p.popular ? 'bg-[#C6A14A] text-[#050816] font-bold hover:bg-[#D4AF5A]' : 'bg-[#050816] text-white hover:bg-[#0a1020]'}`} />
  </div>
);

export const PackageCards = ({ pick, idPrefix = 'package' }) => (
  <div className="md:hidden space-y-6">
    {[...PACKAGE_LIST].sort((a, b) => (b.popular ? 1 : 0) - (a.popular ? 1 : 0)).map((p) => <MobileCard key={p.code} p={p} pick={pick} idPrefix={idPrefix} />)}
  </div>
);
